/* ── Constants ──────────────────────────────────────────────────────────────── */
const SPINWELD_PRODUCTION_BASE = 'https://spinweld-builder-production.up.railway.app';

function getSpinweldApiBase() {
  const configured = (window.SPINWELD_API_BASE || '').replace(/\/$/, '');
  if (configured) return configured;
  // When app.js is embedded on the Shopify page without embed.js, window.location.origin
  // is elkhartrvparts.com. API/PDF routes only live on Railway, so never build
  // order-facing links against the Shopify origin.
  if (window.location.hostname === 'elkhartrvparts.com' || window.location.hostname.endsWith('.myshopify.com')) {
    return SPINWELD_PRODUCTION_BASE;
  }
  return window.location.origin;
}

function apiUrl(path) {
  return getSpinweldApiBase() + path;
}

async function builderJson(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || data.description || 'The request could not be completed. Please try again.');
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('This is taking longer than expected. Your build is still on screen; please try again.');
    throw error;
  } finally { clearTimeout(timer); }
}

function builderStatus(message, error = false) {
  const el = document.getElementById('save-status-line');
  if (!el) return;
  el.textContent = message;
  el.classList.remove('hidden');
  el.setAttribute('role', error ? 'alert' : 'status');
}

function builderTrack(action) {
  // Never send project names, RV text, email, share IDs or fitting labels.
  if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
  const params = new URLSearchParams(location.search);
  const source = window.SPINWELD_FITMENT ? 'fitment' : params.has('tank') ? 'product' : params.has('job') || params.has('project') ? 'shared' : 'direct';
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'spinweld_builder', builder_action: action, builder_source: source, fitting_count: state.placedFittings.length });
}

function updateBuildUrl() {
  const url = new URL(location.href);
  url.searchParams.delete('project');
  url.searchParams.set('job', state.jobId);
  history.replaceState({}, '', url.pathname + url.search + url.hash);
}

const FITTING_COLORS = {
  Drain: '#2563eb',
  Vent: '#16a34a',
  Inlet: '#0891b2',
  Sensor: '#ea580c',
  Bulkhead: '#9333ea',
  Fitting: '#0d9488',
  Sealant: '#b45309',
  Other: '#6b7280',
};

const VIEW_CONFIGS = {
  top:   { label: 'Top',   wDim: 'L', hDim: 'W' },
  front: { label: 'Front', wDim: 'L', hDim: 'H' },
  side:  { label: 'Side',  wDim: 'W', hDim: 'H' },
};

/* ── State ──────────────────────────────────────────────────────────────────── */
const state = {
  tanks: [],
  fittings: [],
  selectedTank: null,
  placedFittings: [],   // { id, fittingId, fittingTitle, fittingSku, fittingType, view, x_inches, y_inches, label }
  activeView: 'top',
  pendingFitting: null, // fitting object waiting to be placed
  hoveredFitting: null,
  dragState: null,      // { fittingStateId, startX, startY, origX, origY }
  jobId: null,
  jobName: null,
  tankFilter: 'all',
  tankSearch: '',
  tankPage: 1,
  TANKS_PER_PAGE: 12,
  fittingTypeFilter: 'all',
  fittingSearch: '',
  pendingPlacement: null, // { fitting, view, x, y } waiting for label confirm
  // Dimension filters
  dimFilters: {
    gallons: { min: null, max: null, unit: 'gal' },
    length:  { min: null, max: null, unit: 'in' },
    width:   { min: null, max: null, unit: 'in' },
    height:  { min: null, max: null, unit: 'in' },
  },
  dimRanges: {
    gallons: { min: 0, max: 100 },
    length:  { min: 0, max: 100 },
    width:   { min: 0, max: 60 },
    height:  { min: 0, max: 60 },
  },
  activeDimFilter: null, // which pill is currently open
};

/* ── Dimension Filter Logic ─────────────────────────────────────────────────── */
function calcDimRanges() {
  const r = { gallons: { min: Infinity, max: -Infinity }, length: { min: Infinity, max: -Infinity }, width: { min: Infinity, max: -Infinity }, height: { min: Infinity, max: -Infinity } };
  state.tanks.forEach(t => {
    const g = parseFloat(t.gallons) || 0;
    const l = parseFloat(t.dimensions && t.dimensions.L) || 0;
    const w = parseFloat(t.dimensions && t.dimensions.W) || 0;
    const h = parseFloat(t.dimensions && t.dimensions.H) || 0;
    if (g > 0) { r.gallons.min = Math.min(r.gallons.min, g); r.gallons.max = Math.max(r.gallons.max, g); }
    if (l > 0) { r.length.min = Math.min(r.length.min, l); r.length.max = Math.max(r.length.max, l); }
    if (w > 0) { r.width.min = Math.min(r.width.min, w); r.width.max = Math.max(r.width.max, w); }
    if (h > 0) { r.height.min = Math.min(r.height.min, h); r.height.max = Math.max(r.height.max, h); }
  });
  ['gallons','length','width','height'].forEach(k => {
    if (r[k].min === Infinity) { r[k].min = 0; r[k].max = 100; }
    state.dimRanges[k] = { min: Math.floor(r[k].min), max: Math.ceil(r[k].max) };
  });
}

function updateDimPills() {
  document.querySelectorAll('.dim-filter-pill').forEach(pill => {
    const dim = pill.dataset.dim;
    const f = state.dimFilters[dim];
    if (f && (f.min !== null || f.max !== null)) {
      pill.classList.add('active');
      const minVal = f.min !== null ? f.min : state.dimRanges[dim].min;
      const maxVal = f.max !== null ? f.max : state.dimRanges[dim].max;
      const label = dim.charAt(0).toUpperCase() + dim.slice(1);
      pill.textContent = `${label}: ${minVal}–${maxVal}${f.unit}`;
    } else {
      pill.classList.remove('active');
      pill.textContent = dim.charAt(0).toUpperCase() + dim.slice(1);
    }
  });
}

function openDimFilterModal(dim) {
  state.activeDimFilter = dim;
  const range = state.dimRanges[dim];
  const filter = state.dimFilters[dim];
  const curMin = filter.min !== null ? filter.min : range.min;
  const curMax = filter.max !== null ? filter.max : range.max;

  document.getElementById('dim-filter-modal-title').textContent = dim.charAt(0).toUpperCase() + dim.slice(1);

  const minSlider = document.getElementById('dim-min-slider');
  const maxSlider = document.getElementById('dim-max-slider');
  const minInput  = document.getElementById('dim-min-input');
  const maxInput  = document.getElementById('dim-max-input');

  [minSlider, maxSlider, minInput, maxInput].forEach(el => {
    el.min = range.min;
    el.max = range.max;
  });
  minSlider.value = curMin;
  maxSlider.value = curMax;
  minInput.value  = curMin;
  maxInput.value  = curMax;

  updateDimSliderLabels();
  updateDimSliderTrack();
  updateDimApplyBtn();

  document.getElementById('dim-filter-modal').style.display = 'flex';
}

function updateDimSliderLabels() {
  const dim = state.activeDimFilter;
  const unit = state.dimFilters[dim].unit;
  const range = state.dimRanges[dim];
  // Labels show the static data-range endpoints (min and max of actual tank data)
  document.getElementById('dim-min-label').textContent = range.min + unit;
  document.getElementById('dim-max-label').textContent = range.max + unit;
}

function updateDimSliderTrack() {
  const minSlider = document.getElementById('dim-min-slider');
  const maxSlider = document.getElementById('dim-max-slider');
  const range = parseFloat(maxSlider.max) - parseFloat(minSlider.min);
  if (range === 0) return;
  const minPct = (parseFloat(minSlider.value) - parseFloat(minSlider.min)) / range * 100;
  const maxPct = (parseFloat(maxSlider.value) - parseFloat(minSlider.min)) / range * 100;
  document.getElementById('dim-slider-track').style.background =
    `linear-gradient(to right, #e0e0e0 0%, #e0e0e0 ${minPct}%, #dc2626 ${minPct}%, #dc2626 ${maxPct}%, #e0e0e0 ${maxPct}%, #e0e0e0 100%)`;
}

function countDimFilterResults(tempFilters) {
  const filters = tempFilters || state.dimFilters;
  const catFilter = state.tankFilter;
  const q = state.tankSearch.toLowerCase().trim();
  return state.tanks.filter(t => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (q && !(t.title.toLowerCase().includes(q) || (t.gallons && String(t.gallons).includes(q)) || (t.sku && t.sku.toLowerCase().includes(q)) || (t.dimensions && `${t.dimensions.L}x${t.dimensions.W}x${t.dimensions.H}`.includes(q)))) return false;
    const g = parseFloat(t.gallons) || 0;
    const l = parseFloat(t.dimensions && t.dimensions.L) || 0;
    const w = parseFloat(t.dimensions && t.dimensions.W) || 0;
    const h = parseFloat(t.dimensions && t.dimensions.H) || 0;
    if (filters.gallons.min !== null && g < filters.gallons.min) return false;
    if (filters.gallons.max !== null && g > filters.gallons.max) return false;
    if (filters.length.min  !== null && l < filters.length.min)  return false;
    if (filters.length.max  !== null && l > filters.length.max)  return false;
    if (filters.width.min   !== null && w < filters.width.min)   return false;
    if (filters.width.max   !== null && w > filters.width.max)   return false;
    if (filters.height.min  !== null && h < filters.height.min)  return false;
    if (filters.height.max  !== null && h > filters.height.max)  return false;
    return true;
  }).length;
}

function updateDimApplyBtn() {
  const dim = state.activeDimFilter;
  const minV = parseInt(document.getElementById('dim-min-slider').value);
  const maxV = parseInt(document.getElementById('dim-max-slider').value);
  // Build temp filters with current slider values
  const tempFilters = JSON.parse(JSON.stringify(state.dimFilters));
  const range = state.dimRanges[dim];
  tempFilters[dim].min = minV <= range.min ? null : minV;
  tempFilters[dim].max = maxV >= range.max ? null : maxV;
  // If min is null and max is null skip this filter entirely for count
  const count = countDimFilterResults(tempFilters);
  document.getElementById('dim-filter-apply').textContent = `Done (${count} tank${count !== 1 ? 's' : ''})`;
}

function closeDimFilterModal() {
  document.getElementById('dim-filter-modal').style.display = 'none';
  state.activeDimFilter = null;
}

function applyDimFilter() {
  // Filtering is already applied live — just close the modal
  closeDimFilterModal();
}

function clearDimFilter() {
  const dim = state.activeDimFilter;
  if (!dim) return;
  state.dimFilters[dim].min = null;
  state.dimFilters[dim].max = null;
  closeDimFilterModal();
  updateDimPills();
  renderTankGrid();
}

function setupDimFilterEvents() {
  // Pill clicks
  document.querySelectorAll('.dim-filter-pill').forEach(pill => {
    pill.addEventListener('click', () => openDimFilterModal(pill.dataset.dim));
  });

  // Close button
  document.getElementById('dim-filter-modal-close').addEventListener('click', closeDimFilterModal);

  // Click outside modal
  document.getElementById('dim-filter-modal').addEventListener('click', (e) => {
    if (e.target === document.getElementById('dim-filter-modal')) closeDimFilterModal();
  });

  // Apply / Clear
  document.getElementById('dim-filter-apply').addEventListener('click', applyDimFilter);
  document.getElementById('dim-filter-clear').addEventListener('click', clearDimFilter);

  // Slider events
  const minSlider = document.getElementById('dim-min-slider');
  const maxSlider = document.getElementById('dim-max-slider');
  const minInput  = document.getElementById('dim-min-input');
  const maxInput  = document.getElementById('dim-max-input');

  function liveApplyFilter() {
    const dim = state.activeDimFilter;
    if (!dim) return;
    const range = state.dimRanges[dim];
    const minV = parseInt(minSlider.value);
    const maxV = parseInt(maxSlider.value);
    state.dimFilters[dim].min = minV <= range.min ? null : minV;
    state.dimFilters[dim].max = maxV >= range.max ? null : maxV;
    updateDimPills();
    renderTankGrid();
    updateDimApplyBtn();
  }

  minSlider.addEventListener('input', () => {
    if (parseInt(minSlider.value) > parseInt(maxSlider.value)) minSlider.value = maxSlider.value;
    minInput.value = minSlider.value;
    updateDimSliderLabels();
    updateDimSliderTrack();
    liveApplyFilter();
  });
  maxSlider.addEventListener('input', () => {
    if (parseInt(maxSlider.value) < parseInt(minSlider.value)) maxSlider.value = minSlider.value;
    maxInput.value = maxSlider.value;
    updateDimSliderLabels();
    updateDimSliderTrack();
    liveApplyFilter();
  });
  minInput.addEventListener('change', () => {
    let v = parseInt(minInput.value);
    const range = state.dimRanges[state.activeDimFilter];
    if (isNaN(v)) v = range.min;
    v = Math.max(range.min, Math.min(v, parseInt(maxSlider.value)));
    minSlider.value = v; minInput.value = v;
    updateDimSliderLabels(); updateDimSliderTrack(); liveApplyFilter();
  });
  maxInput.addEventListener('change', () => {
    let v = parseInt(maxInput.value);
    const range = state.dimRanges[state.activeDimFilter];
    if (isNaN(v)) v = range.max;
    v = Math.min(range.max, Math.max(v, parseInt(minSlider.value)));
    maxSlider.value = v; maxInput.value = v;
    updateDimSliderLabels(); updateDimSliderTrack(); liveApplyFilter();
  });
}

/* ── Helpers ────────────────────────────────────────────────────────────────── */
let idCounter = 0;
function nextId() { return `fit-${Date.now()}-${++idCounter}`; }

function toFraction(val) {
  const whole = Math.floor(val);
  const frac = val - whole;
  const fracs = [
    [0,''], [0.125,'1/8'], [0.25,'1/4'], [0.375,'3/8'],
    [0.5,'1/2'], [0.625,'5/8'], [0.75,'3/4'], [0.875,'7/8'], [1,''],
  ];
  let best = fracs[0], bestDiff = Infinity;
  for (const f of fracs) {
    const d = Math.abs(frac - f[0]);
    if (d < bestDiff) { bestDiff = d; best = f; }
  }
  if (bestDiff > 0.06) return `${val.toFixed(2)}"`;
  if (best[0] === 0) return `${whole}"`;
  if (best[0] === 1) return `${whole + 1}"`;
  return whole > 0 ? `${whole}-${best[1]}"` : `${best[1]}"`;
}

function getViewDims(view) {
  const d = state.selectedTank.dimensions;
  if (view === 'top')   return { W: d.L, H: d.W };
  if (view === 'front') return { W: d.L, H: d.H };
  if (view === 'side')  return { W: d.W, H: d.H };
}

function formatPrice(p) {
  if (!p) return '';
  return '$' + parseFloat(p).toFixed(2);
}

/* ── Canvas drawing ─────────────────────────────────────────────────────────── */
const CANVAS_PAD = 45;
const GRID_STEP_INCHES = 6;

function getCanvasInfo(canvas, viewKey) {
  const dims = getViewDims(viewKey);
  const cw = canvas.width, ch = canvas.height;
  const scaleX = (cw - CANVAS_PAD * 2) / dims.W;
  const scaleY = (ch - CANVAS_PAD * 2) / dims.H;
  const scale = Math.min(scaleX, scaleY);
  const tW = dims.W * scale;
  const tH = dims.H * scale;
  const tX = (cw - tW) / 2;
  const tY = (ch - tH) / 2;
  return { scale, tX, tY, tW, tH, dims };
}

function inchesToCanvas(x_in, y_in, info) {
  return {
    cx: info.tX + (x_in / info.dims.W) * info.tW,
    cy: info.tY + (y_in / info.dims.H) * info.tH,
  };
}

function canvasToInches(cx, cy, info) {
  return {
    x: Math.max(0, Math.min(info.dims.W, (cx - info.tX) / info.tW * info.dims.W)),
    y: Math.max(0, Math.min(info.dims.H, (cy - info.tY) / info.tH * info.dims.H)),
  };
}

function drawDimLine(ctx, x1, y1, x2, y2, label) {
  const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const arrowSize = 6;

  ctx.save();
  ctx.strokeStyle = '#374151';
  ctx.fillStyle = '#374151';
  ctx.lineWidth = 1;

  // Main line
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();

  // Arrowhead at start (pointing inward)
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 + arrowSize * Math.cos(angle + 0.4), y1 + arrowSize * Math.sin(angle + 0.4));
  ctx.lineTo(x1 + arrowSize * Math.cos(angle - 0.4), y1 + arrowSize * Math.sin(angle - 0.4));
  ctx.closePath(); ctx.fill();

  // Arrowhead at end (pointing inward)
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - arrowSize * Math.cos(angle + 0.4), y2 - arrowSize * Math.sin(angle + 0.4));
  ctx.lineTo(x2 - arrowSize * Math.cos(angle - 0.4), y2 - arrowSize * Math.sin(angle - 0.4));
  ctx.closePath(); ctx.fill();

  // Label with white background
  ctx.font = 'bold 10px -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const metrics = ctx.measureText(label);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(midX - metrics.width / 2 - 3, midY - 7, metrics.width + 6, 14);
  ctx.fillStyle = '#374151';
  ctx.fillText(label, midX, midY);
  ctx.restore();
}

function drawView(viewKey) {
  const canvas = document.getElementById(`canvas-${viewKey}`);
  if (!canvas || !state.selectedTank) return;

  // Size canvas to container
  const container = canvas.parentElement;
  const cw = container.clientWidth;
  const aspect = viewKey === 'top'
    ? state.selectedTank.dimensions.W / state.selectedTank.dimensions.L
    : viewKey === 'front'
    ? state.selectedTank.dimensions.H / state.selectedTank.dimensions.L
    : state.selectedTank.dimensions.H / state.selectedTank.dimensions.W;
  const ch = Math.max(140, Math.min(400, cw * aspect + CANVAS_PAD * 2));
  canvas.width = cw;
  canvas.height = ch;
  canvas.style.height = ch + 'px';

  const ctx = canvas.getContext('2d');
  const info = getCanvasInfo(canvas, viewKey);
  const { tX, tY, tW, tH, dims } = info;

  ctx.clearRect(0, 0, cw, ch);

  // Background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, cw, ch);

  // Grid
  ctx.save();
  ctx.strokeStyle = '#f0f0f0';
  ctx.lineWidth = 1;
  const gridPx = GRID_STEP_INCHES * info.scale;
  for (let gx = tX; gx <= tX + tW + 0.5; gx += gridPx) {
    ctx.beginPath(); ctx.moveTo(gx, tY); ctx.lineTo(gx, tY + tH); ctx.stroke();
  }
  for (let gy = tY; gy <= tY + tH + 0.5; gy += gridPx) {
    ctx.beginPath(); ctx.moveTo(tX, gy); ctx.lineTo(tX + tW, gy); ctx.stroke();
  }
  ctx.restore();

  // Tank rect
  ctx.save();
  ctx.strokeStyle = '#1f2937';
  ctx.lineWidth = 2;
  ctx.strokeRect(tX, tY, tW, tH);
  ctx.restore();

  // Blueprint dimension lines
  // Extension lines to bottom dim line
  ctx.save();
  ctx.strokeStyle = '#9ca3af';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(tX, tY + tH); ctx.lineTo(tX, tY + tH + 22); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(tX + tW, tY + tH); ctx.lineTo(tX + tW, tY + tH + 22); ctx.stroke();
  // Extension lines to left dim line
  ctx.beginPath(); ctx.moveTo(tX, tY); ctx.lineTo(tX - 22, tY); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(tX, tY + tH); ctx.lineTo(tX - 22, tY + tH); ctx.stroke();
  ctx.restore();
  // Bottom dimension line (width)
  drawDimLine(ctx, tX, tY + tH + 20, tX + tW, tY + tH + 20, dims.W + '"');
  // Left dimension line (height)
  drawDimLine(ctx, tX - 20, tY + tH, tX - 20, tY, dims.H + '"');

  // Tick marks on top and right edges every 6"
  ctx.save();
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1;
  ctx.fillStyle = '#9ca3af';
  ctx.font = '8px sans-serif';
  ctx.textAlign = 'center';
  for (let i = 0; i <= dims.W; i += GRID_STEP_INCHES) {
    const px = tX + (i / dims.W) * tW;
    ctx.beginPath(); ctx.moveTo(px, tY); ctx.lineTo(px, tY - 4); ctx.stroke();
    if (i > 0 && i < dims.W) ctx.fillText(i + '"', px, tY - 6);
  }
  ctx.textAlign = 'left';
  for (let i = 0; i <= dims.H; i += GRID_STEP_INCHES) {
    const py = tY + (i / dims.H) * tH;
    ctx.beginPath(); ctx.moveTo(tX + tW, py); ctx.lineTo(tX + tW + 4, py); ctx.stroke();
    if (i > 0 && i < dims.H) ctx.fillText(i + '"', tX + tW + 6, py + 3);
  }
  ctx.restore();

  // Placed fittings
  const viewFittings = state.placedFittings.filter(f => f.view === viewKey);
  for (const f of viewFittings) {
    const { cx, cy } = inchesToCanvas(f.x_inches, f.y_inches, info);
    const color = FITTING_COLORS[f.fittingType] || FITTING_COLORS.Other;
    const isHovered = state.hoveredFitting === f.id;
    const isDragging = state.dragState && state.dragState.fittingStateId === f.id;

    // Shadow
    if (isHovered || isDragging) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 10;
    }

    // Circle
    ctx.beginPath();
    ctx.arc(cx, cy, isDragging ? 9 : isHovered ? 8 : 6, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    if (isHovered || isDragging) ctx.restore();

    // Label
    const label = f.label || f.fittingSku || f.fittingTitle.slice(0, 12);
    ctx.save();
    ctx.font = `${isHovered ? 'bold ' : ''}9px sans-serif`;
    const metrics = ctx.measureText(label);
    const lx = cx + 10, ly = cy - 4;
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillRect(lx - 2, ly - 9, metrics.width + 4, 12);
    ctx.fillStyle = '#111827';
    ctx.fillText(label, lx, ly);
    ctx.restore();

    // Measurement lines (always visible, dimmed)
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.globalAlpha = 0.4;
    // from left
    ctx.beginPath(); ctx.moveTo(tX, cy); ctx.lineTo(cx - 8, cy); ctx.stroke();
    // from top
    ctx.beginPath(); ctx.moveTo(cx, tY); ctx.lineTo(cx, cy - 8); ctx.stroke();
    ctx.restore();

    // Measurement text
    ctx.save();
    ctx.font = '9px sans-serif';
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.4;
    // from-left label
    const fromLeft = toFraction(f.x_inches);
    ctx.fillText(fromLeft, tX + 2, cy - 2);
    // from-top label
    const fromTop = toFraction(f.y_inches);
    ctx.fillText(fromTop, cx + 2, tY + 10);
    ctx.restore();
  }

  // Crosshair cursor hint when pending
  if (state.pendingFitting && state.activeView === viewKey) {
    ctx.save();
    ctx.strokeStyle = FITTING_COLORS[state.pendingFitting.type] || '#6b7280';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 4]);
    ctx.globalAlpha = 0.7;
    // small pulsing circle at center
    const cx = tX + tW / 2, cy = tY + tH / 2;
    ctx.beginPath(); ctx.arc(cx, cy, 6, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }
}

function drawAllViews() {
  ['top', 'front', 'side'].forEach(drawView);
  document.dispatchEvent(new Event('spinweld:changed'));
}

/* ── Tank Grid ──────────────────────────────────────────────────────────────── */
function updateCategoryTabs() {
  // Show/hide category tabs based on available tank data
  const categories = new Set(state.tanks.map(t => t.category));
  document.querySelectorAll('.tab-btn[data-cat]').forEach(btn => {
    const cat = btn.dataset.cat;
    if (cat === 'all') return; // always show All
    btn.style.display = categories.has(cat) ? '' : 'none';
  });
}

function makeTankCard(tank) {
  const dims = tank.dimensions;
  const imgHtml = tank.image
    ? `<img class="tank-card-img" src="${tank.image}" alt="${escHtml(tank.title)}" loading="lazy" />`
    : `<div class="tank-card-img-placeholder" aria-hidden="true"></div>`;
  return `
    <div class="tank-card" data-id="${tank.id}">
      ${imgHtml}
      <div class="tank-card-body">
        <div class="tank-card-category">${{ holding: 'Holding Tank', freshwater: 'Fresh Water Tank', gray: 'Gray Water Tank', black: 'Black Water Tank' }[tank.category] || 'Tank'}</div>
        <div class="tank-card-title">${escHtml(tank.title)}</div>
        <div class="tank-card-dims">${dims.L}" × ${dims.W}" × ${dims.H}"</div>
        ${tank.dimensionsNote ? `<div class="text-error" style="font-size:10px">${tank.dimensionsNote}</div>` : ''}
        <div class="tank-card-meta">
          <span class="tank-card-gallons">${tank.gallons ? tank.gallons + ' gal' : ''}</span>
          <span class="tank-card-price">${formatPrice(tank.price)}</span>
        </div>
      </div>
    </div>`;
}

function getFilteredTanks() {
  const filter = state.tankFilter;
  const q = state.tankSearch.toLowerCase().trim();
  return state.tanks.filter(t => {
    if (filter !== 'all' && t.category !== filter) return false;
    if (q && !(t.title.toLowerCase().includes(q) || (t.gallons && String(t.gallons).includes(q)) || (t.sku && t.sku.toLowerCase().includes(q)) || (t.dimensions && `${t.dimensions.L}x${t.dimensions.W}x${t.dimensions.H}`.includes(q)))) return false;
    // Dimension filters
    const g = parseFloat(t.gallons) || 0;
    const l = parseFloat(t.dimensions && t.dimensions.L) || 0;
    const w = parseFloat(t.dimensions && t.dimensions.W) || 0;
    const h = parseFloat(t.dimensions && t.dimensions.H) || 0;
    const df = state.dimFilters;
    if (df.gallons.min !== null && g < df.gallons.min) return false;
    if (df.gallons.max !== null && g > df.gallons.max) return false;
    if (df.length.min  !== null && l < df.length.min)  return false;
    if (df.length.max  !== null && l > df.length.max)  return false;
    if (df.width.min   !== null && w < df.width.min)   return false;
    if (df.width.max   !== null && w > df.width.max)   return false;
    if (df.height.min  !== null && h < df.height.min)  return false;
    if (df.height.max  !== null && h > df.height.max)  return false;
    return true;
  });
}

function renderTankGrid() {
  state.tankPage = 1;
  const grid = document.getElementById('tank-grid');
  const filtered = getFilteredTanks();

  // Update tab counts
  document.querySelectorAll('.tab-btn[data-cat]').forEach(btn => {
    const cat = btn.dataset.cat;
    const count = cat === 'all' ? state.tanks.length : state.tanks.filter(t => t.category === cat).length;
    const label = { all: 'All', holding: 'Holding', freshwater: 'Fresh Water', gray: 'Gray Water', black: 'Black Water' }[cat] || cat;
    btn.textContent = count > 0 ? `${label} (${count})` : label;
    if (cat !== 'all') btn.style.display = count > 0 ? '' : 'none';
  });

  // Update count bar
  const countBar = document.getElementById('tank-result-count');
  if (countBar) {
    const total = state.tanks.length;
    const isFiltered = filtered.length < total;
    countBar.textContent = isFiltered
      ? `Showing ${filtered.length} of ${total} tanks`
      : `${total} tanks`;
    countBar.style.color = isFiltered ? '#dc2626' : '#6b7280';
    countBar.style.fontWeight = isFiltered ? '600' : '400';
  }

  if (filtered.length === 0) {
    grid.innerHTML = '<div class="loading-spinner"><p>No tanks found.</p></div>';
    return;
  }

  const page = filtered.slice(0, state.TANKS_PER_PAGE);
  grid.innerHTML = page.map(makeTankCard).join('');

  // Load more sentinel
  if (filtered.length > state.TANKS_PER_PAGE) {
    const sentinel = document.createElement('div');
    sentinel.id = 'tank-load-more-sentinel';
    sentinel.style.cssText = 'height:40px;display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:13px;';
    sentinel.textContent = `Showing ${state.TANKS_PER_PAGE} of ${filtered.length} · keep scrolling for more`;
    grid.appendChild(sentinel);
    setupTankScrollObserver(filtered);
  }

  bindTankCardClicks(grid);
}

function bindTankCardClicks(grid) {
  grid.querySelectorAll('.tank-card').forEach(el => {
    el.addEventListener('click', () => {
      const tank = state.tanks.find(t => t.id === el.dataset.id);
      if (tank) selectTank(tank);
    });
  });
}

let _tankScrollObserver = null;
function setupTankScrollObserver(filtered) {
  if (_tankScrollObserver) _tankScrollObserver.disconnect();
  const sentinel = document.getElementById('tank-load-more-sentinel');
  if (!sentinel) return;
  _tankScrollObserver = new IntersectionObserver((entries) => {
    if (!entries[0].isIntersecting) return;
    state.tankPage++;
    const grid = document.getElementById('tank-grid');
    const start = (state.tankPage - 1) * state.TANKS_PER_PAGE;
    const end = state.tankPage * state.TANKS_PER_PAGE;
    const newTanks = filtered.slice(start, end);
    if (newTanks.length === 0) { _tankScrollObserver.disconnect(); sentinel.remove(); return; }
    // Insert new cards before sentinel
    newTanks.forEach(tank => {
      const div = document.createElement('div');
      div.innerHTML = makeTankCard(tank).trim();
      const card = div.firstElementChild;
      card.addEventListener('click', () => selectTank(tank));
      grid.insertBefore(card, sentinel);
    });
    const loaded = Math.min(end, filtered.length);
    if (loaded >= filtered.length) {
      _tankScrollObserver.disconnect();
      sentinel.remove();
    } else {
      sentinel.textContent = `Showing ${loaded} of ${filtered.length} — scroll for more`;
    }
  }, { rootMargin: '200px' });
  _tankScrollObserver.observe(sentinel);
}

function escHtml(s) {
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function selectTank(tank) {
  state.selectedTank = tank;
  state.placedFittings = [];
  state.jobId = null;
  state.jobName = null;
  builderTrack('tank_selected');

  document.getElementById('section-tanks').classList.remove('active');
  document.getElementById('section-builder').classList.add('active');

  document.getElementById('tank-info-title').textContent = tank.title;
  const d = tank.dimensions;
  document.getElementById('tank-info-dims').textContent = `${d.L}" × ${d.W}" × ${d.H}"${tank.gallons ? ' · ' + tank.gallons + ' gal' : ''}`;
  document.getElementById('dims-top').textContent = `(${d.L}" × ${d.W}")`;
  document.getElementById('dims-front').textContent = `(${d.L}" × ${d.H}")`;
  document.getElementById('dims-side').textContent = `(${d.W}" × ${d.H}")`;

  updateJobStatus();
  updateSaveButtonState();
  updateOrderSummary();
  document.getElementById('btn-pdf').disabled = false;

  setActiveView('top');
  requestAnimationFrame(() => { drawAllViews(); updateMeasurementsList(); });
}

/* ── Fitting Catalog ─────────────────────────────────────────────────────────── */
function renderFittingList() {
  const list = document.getElementById('fitting-list');
  const search = state.fittingSearch.toLowerCase();
  const typeF = state.fittingTypeFilter;

  const fittings = state.fittings.filter(f => {
    if (typeF !== 'all' && f.type !== typeF) return false;
    if (search && !f.title.toLowerCase().includes(search) && !f.sku.toLowerCase().includes(search)) return false;
    return true;
  });

  if (fittings.length === 0) {
    list.innerHTML = '<p class="empty-hint">No fittings match.</p>';
    return;
  }

  list.innerHTML = fittings.map(f => {
    const color = FITTING_COLORS[f.type] || FITTING_COLORS.Other;
    const imgHtml = f.image
      ? `<img class="fitting-img" src="${f.image}" alt="" loading="lazy" />`
      : `<div class="fitting-img-placeholder" aria-hidden="true"></div>`;
    const isSelected = state.pendingFitting && state.pendingFitting.id === f.id;
    return `
      <div class="fitting-item${isSelected ? ' selected' : ''}" data-id="${f.id}">
        ${imgHtml}
        <div class="fitting-info">
          <div class="fitting-name">${escHtml(f.title)}</div>
          <div class="fitting-meta">
            <span class="fitting-type-badge" style="background:${color}">${f.type}</span>
            ${f.sku ? `<span class="fitting-sku">${escHtml(f.sku)}</span>` : ''}
          </div>
        </div>
        <span class="fitting-price">${formatPrice(f.price)}</span>
      </div>`;
  }).join('');

  list.querySelectorAll('.fitting-item').forEach(el => {
    el.addEventListener('click', () => {
      const fitting = state.fittings.find(f => f.id === el.dataset.id);
      if (fitting) selectFitting(fitting);
    });
  });
}

function selectFitting(fitting) {
  if (state.pendingFitting && state.pendingFitting.id === fitting.id) {
    state.pendingFitting = null;
  } else {
    state.pendingFitting = fitting;
  }
  renderFittingList();
  updateViewHighlights();
}

/* ── Active View ─────────────────────────────────────────────────────────────── */
function setActiveView(viewKey) {
  state.activeView = viewKey;
  updateViewHighlights();
}

function updateViewHighlights() {
  ['top', 'front', 'side'].forEach(v => {
    const panel = document.getElementById(`view-${v}`);
    panel.classList.toggle('active-view', v === state.activeView);
    panel.classList.toggle('has-pending', !!state.pendingFitting && v === state.activeView);
  });
}

/* ── Canvas Interaction ──────────────────────────────────────────────────────── */
function setupCanvasEvents() {
  ['top', 'front', 'side'].forEach(viewKey => {
    const canvas = document.getElementById(`canvas-${viewKey}`);
    const panel = document.getElementById(`view-${viewKey}`);

    panel.addEventListener('click', (e) => {
      if (e.target === panel || e.target.classList.contains('view-label') || e.target.classList.contains('view-dims')) {
        setActiveView(viewKey);
      }
    });

    canvas.addEventListener('mousedown', (e) => {
      setActiveView(viewKey);
      if (e.button !== 0) return;

      const rect = canvas.getBoundingClientRect();
      const px = (e.clientX - rect.left) * (canvas.width / rect.width);
      const py = (e.clientY - rect.top) * (canvas.height / rect.height);
      const info = getCanvasInfo(canvas, viewKey);

      // Check if clicking an existing fitting
      const hit = hitTestFitting(px, py, viewKey, info);

      if (hit) {
        // Start drag
        state.dragState = {
          fittingStateId: hit.id,
          canvas, viewKey, info,
          startPx: px, startPy: py,
          origX: hit.x_inches, origY: hit.y_inches,
        };
        showTooltip(hit, e.clientX, e.clientY, viewKey, info);
        e.preventDefault();
        return;
      }

      // Place pending fitting
      if (state.pendingFitting) {
        const { x, y } = canvasToInches(px, py, info);
        // Auto-place immediately, no label modal
        const label = state.pendingFitting.sku || state.pendingFitting.title.slice(0, 20);
        placeFitting(state.pendingFitting, viewKey, x, y, label);
        state.pendingFitting = null;
        renderFittingList();
        updateViewHighlights();
        return;
      }
    });

    canvas.addEventListener('mousemove', (e) => {
      const rect = canvas.getBoundingClientRect();
      const px = (e.clientX - rect.left) * (canvas.width / rect.width);
      const py = (e.clientY - rect.top) * (canvas.height / rect.height);
      const info = getCanvasInfo(canvas, viewKey);

      if (state.dragState && state.dragState.viewKey === viewKey) {
        // Dragging
        const { x, y } = canvasToInches(px, py, info);
        const f = state.placedFittings.find(f => f.id === state.dragState.fittingStateId);
        if (f) {
          f.x_inches = Math.round(x * 4) / 4; // snap to 0.25"
          f.y_inches = Math.round(y * 4) / 4;
          drawView(viewKey);
          updateMeasurementsList();
          showTooltip(f, e.clientX, e.clientY, viewKey, info);
        }
        return;
      }

      // Hover
      const hit = hitTestFitting(px, py, viewKey, info);
      const prevHovered = state.hoveredFitting;
      state.hoveredFitting = hit ? hit.id : null;

      if (state.hoveredFitting !== prevHovered) {
        drawView(viewKey);
        if (hit) {
          showTooltip(hit, e.clientX, e.clientY, viewKey, info);
        } else {
          hideTooltip();
        }
      }
      if (hit) {
        showTooltip(hit, e.clientX, e.clientY, viewKey, info);
        canvas.style.cursor = 'grab';
      } else {
        canvas.style.cursor = state.pendingFitting ? 'crosshair' : 'default';
      }
    });

    canvas.addEventListener('mouseup', () => {
      if (state.dragState) {
        state.dragState = null;
        drawAllViews();
      }
    });

    canvas.addEventListener('mouseleave', () => {
      if (!state.dragState) {
        state.hoveredFitting = null;
        drawView(viewKey);
        hideTooltip();
      }
    });

    // Touch support
    canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      setActiveView(viewKey);
      const rect = canvas.getBoundingClientRect();
      const px = (touch.clientX - rect.left) * (canvas.width / rect.width);
      const py = (touch.clientY - rect.top) * (canvas.height / rect.height);
      const info = getCanvasInfo(canvas, viewKey);
      const hit = hitTestFitting(px, py, viewKey, info);

      if (hit) {
        state.dragState = { fittingStateId: hit.id, canvas, viewKey, info };
        return;
      }
      if (state.pendingFitting) {
        const { x, y } = canvasToInches(px, py, info);
        // On touch: skip label modal, auto-place immediately
        const label = state.pendingFitting.sku || state.pendingFitting.title.slice(0, 20);
        placeFitting(state.pendingFitting, viewKey, x, y, label);
        state.pendingFitting = null;
        renderFittingList();
        updateViewHighlights();
      }
    }, { passive: false });

    canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!state.dragState || state.dragState.viewKey !== viewKey) return;
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const px = (touch.clientX - rect.left) * (canvas.width / rect.width);
      const py = (touch.clientY - rect.top) * (canvas.height / rect.height);
      const info = getCanvasInfo(canvas, viewKey);
      const { x, y } = canvasToInches(px, py, info);
      const f = state.placedFittings.find(f => f.id === state.dragState.fittingStateId);
      if (f) {
        f.x_inches = Math.round(x * 4) / 4;
        f.y_inches = Math.round(y * 4) / 4;
        drawView(viewKey);
        updateMeasurementsList();
      }
    }, { passive: false });

    canvas.addEventListener('touchend', () => {
      if (state.dragState) { state.dragState = null; drawAllViews(); }
    });
  });
}

function hitTestFitting(px, py, viewKey, info) {
  const fittings = state.placedFittings.filter(f => f.view === viewKey);
  for (let i = fittings.length - 1; i >= 0; i--) {
    const f = fittings[i];
    const { cx, cy } = inchesToCanvas(f.x_inches, f.y_inches, info);
    const dist = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2);
    if (dist <= 12) return f;
  }
  return null;
}

/* ── Tooltip ─────────────────────────────────────────────────────────────────── */
function showTooltip(fitting, clientX, clientY, viewKey, info) {
  const tooltip = document.getElementById('fitting-tooltip');
  const title = tooltip.querySelector('.tooltip-title');
  const meas = tooltip.querySelector('.tooltip-measurements');

  const dims = getViewDims(viewKey);
  const fromRight = dims.W - fitting.x_inches;
  const fromBottom = dims.H - fitting.y_inches;

  title.textContent = fitting.label || fitting.fittingTitle;
  meas.innerHTML = `
    From left: <span>${toFraction(fitting.x_inches)}</span><br>
    From top: <span>${toFraction(fitting.y_inches)}</span><br>
    From right: <span>${toFraction(fromRight)}</span><br>
    From bottom: <span>${toFraction(fromBottom)}</span>
  `;

  tooltip.querySelector('.tooltip-delete').dataset.fittingId = fitting.id;

  const vw = window.innerWidth, vh = window.innerHeight;
  let tx = clientX + 14, ty = clientY - 10;
  if (tx + 200 > vw) tx = clientX - 200;
  if (ty + 140 > vh) ty = vh - 145;
  tooltip.style.left = tx + 'px';
  tooltip.style.top = ty + 'px';
  tooltip.classList.remove('hidden');
}

function hideTooltip() {
  if (!state.dragState) {
    document.getElementById('fitting-tooltip').classList.add('hidden');
  }
}

document.getElementById('fitting-tooltip').querySelector('.tooltip-delete').addEventListener('click', (e) => {
  const id = e.target.dataset.fittingId;
  if (id) deleteFitting(id);
});

function deleteFitting(id) {
  state.placedFittings = state.placedFittings.filter(f => f.id !== id);
  state.hoveredFitting = null;
  hideTooltip();
  drawAllViews();
  updateMeasurementsList();
  updateOrderSummary();
}

/* ── Label Modal ─────────────────────────────────────────────────────────────── */
function showLabelModal() {
  const fitting = state.pendingPlacement.fitting;
  document.getElementById('input-fitting-label').value = fitting.title.slice(0, 30);
  openModal('modal-label');
}

// btn-label-confirm removed with label modal - guard against null
if (document.getElementById('btn-label-confirm')) {
  document.getElementById('btn-label-confirm').addEventListener('click', () => {
    if (!state.pendingPlacement) return;
    const { fitting, view, x, y } = state.pendingPlacement;
    const label = document.getElementById('input-fitting-label').value.trim() || fitting.sku || fitting.title.slice(0, 20);
    placeFitting(fitting, view, x, y, label);
    closeModal('modal-label');
    state.pendingPlacement = null;
  });
}

function placeFitting(fitting, view, x_inches, y_inches, label) {
  if (state.placedFittings.length >= 80) { builderStatus('A drawing supports up to 80 fittings.', true); return; }
  state.placedFittings.push({
    id: nextId(),
    fittingId: fitting.id,
    fittingTitle: fitting.title,
    fittingSku: fitting.sku,
    fittingType: fitting.type,
    fittingVariantId: fitting.variantId || null,
    fittingPrice: fitting.price || null,
    view,
    x_inches: Math.round(x_inches * 4) / 4,
    y_inches: Math.round(y_inches * 4) / 4,
    label,
  });
  state.pendingFitting = null;
  renderFittingList();
  updateViewHighlights();
  drawAllViews();
  updateMeasurementsList();
  updateOrderSummary();
}

/* ── Measurements List ───────────────────────────────────────────────────────── */
function updateMeasurementsList() {
  const list = document.getElementById('measurements-list');
  if (state.placedFittings.length === 0) {
    list.innerHTML = '<p class="empty-hint">No fittings placed yet. Select a fitting on the right and click on a view to place it.</p>';
    return;
  }

  list.innerHTML = state.placedFittings.map(f => {
    const color = FITTING_COLORS[f.fittingType] || FITTING_COLORS.Other;
    const dims = getViewDims(f.view);
    const fromRight = dims.W - f.x_inches;
    const fromBottom = dims.H - f.y_inches;
    return `
      <div class="measurement-item" data-id="${f.id}">
        <div class="fitting-dot" style="background:${color}"></div>
        <span class="measurement-name">${escHtml(f.label || f.fittingTitle)}</span>
        <span class="measurement-view">${f.view.toUpperCase()}</span>
        <span class="measurement-coords">${toFraction(f.x_inches)} / ${toFraction(f.y_inches)} · ${toFraction(fromRight)} / ${toFraction(fromBottom)}</span>
        <button class="measurement-del" data-id="${f.id}" title="Delete">✕</button>
      </div>`;
  }).join('');

  list.querySelectorAll('.measurement-del').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      deleteFitting(btn.dataset.id);
    });
  });

  list.querySelectorAll('.measurement-item').forEach(el => {
    el.addEventListener('mouseenter', () => {
      state.hoveredFitting = el.dataset.id;
      drawAllViews();
    });
    el.addEventListener('mouseleave', () => {
      state.hoveredFitting = null;
      drawAllViews();
    });
  });
}

/* ── Save Job ────────────────────────────────────────────────────────────────── */
function updateJobStatus() {
  const el = document.getElementById('job-status');
  if (!el) return; // job-status is in site-header which is hidden in embed mode
  if (state.jobId) {
    el.textContent = `Job: ${state.jobName || state.jobId.slice(0, 8)}`;
  } else if (state.selectedTank) {
    el.textContent = 'Unsaved';
  } else {
    el.textContent = '';
  }
}

// Sharing a draft does not require contact information.
function updateSaveButtonState() {
  const saveBtn = document.getElementById('btn-save');
  if (saveBtn) {
    saveBtn.disabled = !state.selectedTank;
  }
}

// has-value highlight for project fields
['input-job-name', 'input-job-email', 'input-rv-make', 'input-rv-model', 'input-rv-year'].forEach(id => {
  const el = document.getElementById(id);
  if (!el) return;
  el.addEventListener('input', () => {
    el.classList.toggle('has-value', el.value.trim().length > 0);
  });
});

const jobEmailEl = document.getElementById('input-job-email');
if (jobEmailEl) jobEmailEl.addEventListener('input', updateSaveButtonState);

document.getElementById('btn-save').addEventListener('click', async () => {
  if (!state.selectedTank) return;
  const jobName = document.getElementById('input-job-name').value.trim();

  const saveBtn = document.getElementById('btn-save');
  saveBtn.textContent = 'Saving…';
  saveBtn.disabled = true;

  try {
    const id = await saveCurrentJob({ context: 'manual-save' });

    state.jobId = id;
    state.jobName = jobName || 'My Tank Build';
    updateJobStatus();

    const shareUrl = 'https://elkhartrvparts.com/pages/custom-rv-tank-builder?job=' + state.jobId;
    document.getElementById('share-link-input').value = shareUrl;
    const conf = document.getElementById('save-confirmation');
    if (conf) conf.classList.remove('hidden');
    const statusLine = document.getElementById('save-status-line');
    if (statusLine) {
      statusLine.textContent = 'Build saved. Copy the link below to share this version.';
      statusLine.classList.remove('hidden');
    }

    // Note: don't update browser URL — we're embedded in Shopify
  } catch (err) {
    builderStatus('Could not save: ' + err.message, true);
  } finally {
    saveBtn.textContent = 'Save & share this build';
    updateSaveButtonState();
  }
});

document.getElementById('btn-copy-link').addEventListener('click', async () => {
  const input = document.getElementById('share-link-input');
  try {
    await navigator.clipboard.writeText(input.value);
  } catch {
    input.select();
    if (!document.execCommand('copy')) { builderStatus('Select the link above and copy it to share your build.', true); return; }
  }
  document.getElementById('btn-copy-link').textContent = 'Copied!';
  setTimeout(() => { document.getElementById('btn-copy-link').textContent = 'Copy'; }, 2000);
});

/* ── PDF Generation ──────────────────────────────────────────────────────────── */
document.getElementById('btn-pdf').addEventListener('click', async () => {
  if (!state.selectedTank) return;
  const button = document.getElementById('btn-pdf');
  button.disabled = true;
  // Open during the tap, before awaiting the save, to avoid mobile popup blocking.
  const preview = window.open('about:blank', '_blank');
  if (preview) preview.opener = null;

  // Always save the latest fitting placements before opening the print.
  try {
    await saveCurrentJob({ context: 'pdf' });
    updateBuildUrl();
    const url = apiUrl(`/api/jobs/${state.jobId}/pdf`);
    if (preview) preview.location.replace(url);
    else {
      builderStatus('Your drawing is ready.');
      const link = document.createElement('a');
      link.href = url; link.target = '_blank'; link.rel = 'noopener'; link.textContent = ' Open installation drawing';
      document.getElementById('save-status-line').append(link);
    }
  } catch (err) {
    if (preview) preview.close();
    builderStatus('Could not create the drawing: ' + err.message, true);
  } finally { button.disabled = false; }
});

/* ── Change Tank ─────────────────────────────────────────────────────────────── */
document.getElementById('btn-change-tank').addEventListener('click', () => {
  if (state.placedFittings.length > 0) {
    if (!confirm('Going back will clear your placed fittings. Continue?')) return;
  }
  state.selectedTank = null;
  state.placedFittings = [];
  state.pendingFitting = null;
  state.jobId = null;
  document.getElementById('section-builder').classList.remove('active');
  document.getElementById('section-tanks').classList.add('active');
  document.getElementById('btn-save').disabled = true;
  document.getElementById('btn-pdf').disabled = true;
  updateJobStatus();
  updateSaveButtonState();
});

/* ── Tab / Filter Buttons ────────────────────────────────────────────────────── */
const tankSearchEl = document.getElementById('tank-search');
if (tankSearchEl) {
  tankSearchEl.addEventListener('input', () => {
    state.tankSearch = tankSearchEl.value;
    renderTankGrid();
  });
}

document.querySelectorAll('.tab-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    state.tankFilter = btn.dataset.cat;
    renderTankGrid();
  });
});

document.querySelectorAll('#fitting-type-filters .chip-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('#fitting-type-filters .chip-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    state.fittingTypeFilter = btn.dataset.type;
    renderFittingList();
  });
});

document.getElementById('fitting-search').addEventListener('input', (e) => {
  state.fittingSearch = e.target.value;
  renderFittingList();
});

/* ── Modal Helpers ───────────────────────────────────────────────────────────── */
function openModal(id) { document.getElementById(id).classList.remove('hidden'); }
function closeModal(id) { document.getElementById(id).classList.add('hidden'); }

document.querySelectorAll('.modal-close, [data-modal]').forEach(el => {
  el.addEventListener('click', (e) => {
    const target = e.currentTarget.closest('.modal-overlay') || document.getElementById(e.currentTarget.dataset.modal);
    if (target) target.classList.add('hidden');
  });
});
document.querySelectorAll('.modal-overlay').forEach(overlay => {
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.classList.add('hidden');
  });
});

/* ── Load Job from URL ───────────────────────────────────────────────────────── */
async function loadJobFromUrl() {
  // Support both ?job=UUID (Shopify embed) and /job/UUID (direct Railway URL)
  const params = new URLSearchParams(location.search);
  const qJobId = params.get('job');
  const match = location.pathname.match(/^\/job\/([a-f0-9-]+)$/);
  if (!qJobId && !match) return false;
  const jobId = qJobId || match[1];
  try {
    const job = await builderJson(apiUrl(`/api/jobs/${encodeURIComponent(jobId)}`));
    const savedDimensions = job.tank_dimensions;
    if (!savedDimensions || !['L', 'W', 'H'].every(key => Number(savedDimensions[key]) > 0)) throw new Error('This older build has no usable dimensions. Select its tank to start a new drawing.');

    // We need tanks loaded first to find the matching tank object
    // Create a minimal tank object from job data
    const tank = {
      id: job.tank_product_id || '',
      title: job.tank_title || 'Unknown Tank',
      category: 'holding',
      dimensions: savedDimensions,
      gallons: null,
      image: null,
      dimensionsNote: null,
    };

    // Try to find real tank from loaded tanks
    const realTank = state.tanks.find(t => String(t.id) === String(job.tank_product_id));
    Object.assign(tank, realTank || {}, {
      dimensions: savedDimensions,
    });

    if (job.tank_title) tank.title = job.tank_title;
    const gallonsMatch = (job.tank_title || '').match(/(\d+(?:\.\d+)?)\s*[Gg]allon/);
    if (gallonsMatch) tank.gallons = parseFloat(gallonsMatch[1]);

    // Select first: selectTank deliberately clears the previous build.
    selectTank(tank);
    state.jobId = job.id;
    state.jobName = job.job_name;
    state.placedFittings = (job.fittings || []).map(f => ({ ...f, id: f.id || nextId() }));

    const restoredFields = {
      'input-job-name': job.job_name,
      'input-job-email': job.email,
      'input-rv-make': job.rv_make,
      'input-rv-model': job.rv_model,
      'input-rv-year': job.rv_year,
    };
    Object.entries(restoredFields).forEach(([id, value]) => {
      const input = document.getElementById(id);
      if (input && value) {
        input.value = value;
        input.classList.add('has-value');
      }
    });
    const fitmentConfirmed = document.getElementById('input-fitment-confirmed');
    if (fitmentConfirmed) fitmentConfirmed.checked = Boolean(job.fitment_confirmed);

    drawAllViews();
    updateMeasurementsList();
    updateOrderSummary();
    updateJobStatus();
    return true;
  } catch (err) {
    console.error('Failed to load job:', err);
    const notice = document.createElement('p');
    notice.className = 'error-banner'; notice.setAttribute('role', 'alert');
    notice.textContent = 'Your shared build could not be loaded. ' + err.message;
    document.getElementById('section-tanks').prepend(notice);
    return false;
  }
}

/* ── Load a specific tank from a PDP or permanent fitment page ─────────────── */
function normalizeTankKey(value) {
  return String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '');
}

function loadTankFromUrl() {
  const params = new URLSearchParams(location.search);
  const requested = params.get('tank') || params.get('tank_handle') || params.get('tank_id');
  const part = params.get('part') || params.get('sku');
  if (!requested && !part) return false;

  const keys = [requested, part].filter(Boolean).map(normalizeTankKey);
  const tank = state.tanks.find(candidate => {
    const exactValues = [candidate.id, candidate.variantId, candidate.handle].map(normalizeTankKey);
    if (keys.some(key => exactValues.includes(key))) return true;
    const searchable = normalizeTankKey([candidate.handle, candidate.title].filter(Boolean).join(' '));
    return keys.some(key => key.length >= 3 && searchable.includes(key));
  });

  if (!tank) {
    state.tankSearch = part || requested || '';
    const search = document.getElementById('tank-search');
    if (search) search.value = state.tankSearch;
    renderTankGrid();
    return false;
  }

  selectTank(tank);
  const source = params.get('from');
  if (source === 'product') {
    const info = document.getElementById('tank-info-bar');
    if (info) info.setAttribute('data-entry-source', 'product');
  }
  return true;
}

function loadTankFitment() {
  const fitment = window.SPINWELD_FITMENT;
  if (!fitment || (!fitment.tankHandle && !fitment.tankPart)) return false;
  const keys = [fitment.tankHandle, fitment.tankPart].filter(Boolean).map(normalizeTankKey);
  const tank = state.tanks.find(candidate => {
    const exactValues = [candidate.id, candidate.variantId, candidate.handle].map(normalizeTankKey);
    if (keys.some(key => exactValues.includes(key))) return true;
    const searchable = normalizeTankKey([candidate.handle, candidate.title].filter(Boolean).join(' '));
    return keys.some(key => key.length >= 3 && searchable.includes(key));
  });
  if (!tank) return false;

  selectTank(tank);
  const configuration = Array.isArray(fitment.configuration) ? fitment.configuration : [];
  state.placedFittings = configuration.map((item, index) => {
    const skuKey = normalizeTankKey(item.fittingSku || item.sku);
    const catalogFitting = state.fittings.find(fitting => normalizeTankKey(fitting.sku) === skuKey);
    return {
      id: item.id || `fitment-${index + 1}`,
      fittingId: (catalogFitting && catalogFitting.id) || item.fittingId || '',
      fittingTitle: (catalogFitting && catalogFitting.title) || item.fittingTitle || item.label || 'Tank fitting',
      fittingSku: (catalogFitting && catalogFitting.sku) || item.fittingSku || item.sku || '',
      fittingType: (catalogFitting && catalogFitting.type) || item.fittingType || 'Fitting',
      fittingPrice: (catalogFitting && catalogFitting.price) || item.fittingPrice || 0,
      view: ['top', 'front', 'side'].includes(item.view) ? item.view : 'top',
      x_inches: Number(item.x_inches) || 0,
      y_inches: Number(item.y_inches) || 0,
      label: item.label || item.fittingSku || item.sku || '',
    };
  });
  updateOrderSummary();
  updateSaveButtonState();
  requestAnimationFrame(() => { drawAllViews(); updateMeasurementsList(); });
  return true;
}

/* ── Order Summary ───────────────────────────────────────────────────────────── */
function updateOrderSummary() {
  if (cartSubmitting) return;
  const summaryEl = document.getElementById('order-summary');
  const cartBtn = document.getElementById('btn-add-to-cart');

  if (!summaryEl || !cartBtn) return;

  const tank = state.selectedTank;
  if (!tank) {
    summaryEl.classList.add('hidden');
    cartBtn.disabled = true;
    cartBtn.textContent = 'Add to Cart';
    return;
  }

  summaryEl.classList.remove('hidden');

  const tankPrice = parseFloat(tank.price) || 0;
  const fittingCount = state.placedFittings.length;
  const fittingTotal = state.placedFittings.reduce((sum, f) => sum + (parseFloat(f.fittingPrice) || 0), 0);
  const installFee = fittingCount * 50;
  const identicalCount = Math.max(1, Math.min(20, Number(document.querySelector('[data-quantity]')?.value) || 1));
  const grandTotal = (tankPrice + fittingTotal + installFee) * identicalCount;

  const rows = document.getElementById('order-summary-rows');
  if (rows) {
    let html = `<div class="order-row"><span>Tank: ${escHtml(tank.title.length > 40 ? tank.title.slice(0, 40) + '…' : tank.title)}</span><span>${tankPrice > 0 ? '$' + tankPrice.toFixed(2) : '—'}</span></div>`;
    if (fittingCount > 0) {
      html += `<div class="order-row"><span>${fittingCount} Fitting${fittingCount > 1 ? 's' : ''}</span><span>${fittingTotal > 0 ? '$' + fittingTotal.toFixed(2) : '—'}</span></div>`;
      html += `<div class="order-row"><span>Installation Fee (${fittingCount} × $50)</span><span>$${installFee.toFixed(2)}</span></div>`;
    }
    html += `<div class="order-row order-total-row"><span>Estimated total${identicalCount > 1 ? ` · ${identicalCount} identical tanks` : ''}</span><span>${grandTotal > 0 ? '$' + grandTotal.toFixed(2) : '—'}</span></div>`;
    rows.innerHTML = html;
  }

  cartBtn.disabled = false;
  cartBtn.textContent = grandTotal > 0
    ? `Add configured tank to cart · $${grandTotal.toFixed(2)}`
    : 'Add configured tank to cart';
}

/* ── Add to Cart ─────────────────────────────────────────────────────────────── */
async function saveCurrentJob(options = {}) {
  if (!state.selectedTank) throw new Error('No tank selected');
  const jobName = (document.getElementById('input-job-name') && document.getElementById('input-job-name').value.trim()) || state.jobName || 'My Tank Build';
  const email = (document.getElementById('input-job-email') && document.getElementById('input-job-email').value.trim()) || '';
  const rvMake = (document.getElementById('input-rv-make') && document.getElementById('input-rv-make').value.trim()) || '';
  const rvModel = (document.getElementById('input-rv-model') && document.getElementById('input-rv-model').value.trim()) || '';
  const rvYear = (document.getElementById('input-rv-year') && document.getElementById('input-rv-year').value.trim()) || '';
  const fitmentConfirmed = Boolean(document.getElementById('input-fitment-confirmed') && document.getElementById('input-fitment-confirmed').checked);
  const fittingSnapshot = state.placedFittings.map(f => ({ ...f }));
  const body = {
    email: email || undefined,
    job_name: jobName,
    tank_product_id: state.selectedTank.id,
    tank_handle: state.selectedTank.handle,
    tank_part_number: state.selectedTank.sku || '',
    tank_title: state.selectedTank.title,
    tank_dimensions: state.selectedTank.dimensions,
    fittings: fittingSnapshot,
    client_fitting_count: fittingSnapshot.length,
    save_context: options.context || 'manual',
    rv_make: rvMake || undefined,
    rv_model: rvModel || undefined,
    rv_year: rvYear ? Number(rvYear) : undefined,
    fitment_confirmed: fitmentConfirmed && Boolean(rvMake && rvModel && rvYear),
  };
  const url = state.jobId ? apiUrl(`/api/jobs/${state.jobId}`) : apiUrl('/api/jobs');
  const data = await builderJson(url, {
    method: state.jobId ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  state.jobId = data.id;
  state.jobName = jobName;
  builderTrack('build_saved');
  updateJobStatus();
  return state.jobId;
}

async function autoSaveForCart() {
  // Always write the current tank + placed fittings before cart add, even when a
  // job already exists. The old early-return could leave PDFs stuck at the
  // earlier saved state (often Fittings: 0).
  const jobId = await saveCurrentJob({ context: 'cart' });
  updateBuildUrl();
  return jobId;
}

const cartBtn = document.getElementById('btn-add-to-cart');
let tankProject = null;
let cartSubmitting = false;
if (cartBtn) {
  cartBtn.addEventListener('click', async () => {
    if (!state.selectedTank || cartSubmitting) return;
    cartSubmitting = true;
    cartBtn.disabled = true;
    cartBtn.textContent = 'Adding to cart…';

    try {
      if (tankProject) tankProject.validate();
      if (state.placedFittings.length === 0) {
        const proceed = confirm('No fittings are placed on this tank. Continue only if the customer wants a blank tank with no spinweld installation work.');
        if (!proceed) {
          cartSubmitting = false;
          cartBtn.disabled = false;
          updateOrderSummary();
          return;
        }
      }

      // Step 1: auto-save the latest fitting placements
      const jobId = await autoSaveForCart();
      const pdfUrl = apiUrl('/api/jobs/' + jobId + '/pdf');

      // Step 2: fetch config
      const cfg = await builderJson(apiUrl('/api/config'));
      const installFeeVariantId = cfg.installationFeeVariantId;

      // Step 3: build items
      const items = [];
      const tank = state.selectedTank;
      const d = tank.dimensions;
      if (!tank.variantId) throw new Error('This saved tank is no longer available to order. Choose a current tank to continue.');
      if (state.placedFittings.some(f => !f.fittingVariantId)) throw new Error('A fitting is no longer available to order. Replace it before continuing.');
      if (state.placedFittings.length && !installFeeVariantId) throw new Error('Installation pricing could not be loaded. Please try again.');
      const tankItem = {
        id: tank.variantId,
        quantity: tankProject ? tankProject.quantity() : 1,
        properties: {
          'Job ID': jobId,
          'View Installation PDF': pdfUrl,
          'Tank': tank.title,
          'Dimensions': `${d.L}" × ${d.W}" × ${d.H}"`,
          'Return Policy': 'Final Sale - No Returns',
          '_Spinweld Job ID': jobId,
          '_Spinweld Component': 'tank',
          '_Spinweld Units Per Tank': '1',
        },
      };
      const rvMake = (document.getElementById('input-rv-make') && document.getElementById('input-rv-make').value.trim()) || '';
      const rvModel = (document.getElementById('input-rv-model') && document.getElementById('input-rv-model').value.trim()) || '';
      const rvYear = (document.getElementById('input-rv-year') && document.getElementById('input-rv-year').value.trim()) || '';
      const fitmentConfirmed = Boolean(document.getElementById('input-fitment-confirmed') && document.getElementById('input-fitment-confirmed').checked && rvMake && rvModel && rvYear);
      if (rvMake) tankItem.properties['RV Make'] = rvMake;
      if (rvModel) tankItem.properties['RV Model'] = rvModel;
      if (rvYear) tankItem.properties['RV Year'] = rvYear;
      if (fitmentConfirmed) tankItem.properties['Customer Fitment Confirmation'] = 'Measured and confirmed';
      if (tankItem.id) items.push(tankItem);

      // Add each fitting
      state.placedFittings.forEach(f => {
        if (f.fittingVariantId) {
          items.push({ id: f.fittingVariantId, quantity: tankItem.quantity, properties: { 'Return Policy': 'Final Sale - No Returns', '_Spinweld Job ID': jobId, '_Spinweld Component': 'fitting', '_Spinweld Fitting ID': f.id, '_Spinweld Units Per Tank': '1', 'Tank Build': state.jobName || 'My Tank Build', 'View Installation PDF': pdfUrl } });
        }
      });

      // Installation fee line item (qty = number of fittings)
      const fittingCount = state.placedFittings.length;
      if (fittingCount > 0 && installFeeVariantId) {
        items.push({ id: installFeeVariantId, quantity: fittingCount * tankItem.quantity, properties: { 'Return Policy': 'Final Sale - No Returns', '_Spinweld Job ID': jobId, '_Spinweld Component': 'installation', '_Spinweld Units Per Tank': String(fittingCount), 'Tank Build': state.jobName || 'My Tank Build', 'View Installation PDF': pdfUrl } });
      }

      if (items.length === 0) {
        alert('No items with purchasable variants found. Please contact us.');
        cartBtn.disabled = false;
        updateOrderSummary();
        return;
      }

      // Group-aware path verifies the complete replacement before removing the old build.
      if (window.SpinweldCart) {
        items.forEach(item => { item.properties['_Spinweld Fitting Count'] = String(fittingCount); });
        const client = window.SpinweldCart.createClient({ storage: sessionStorage,
          request: (path, body) => builderJson((window.Shopify?.routes?.root || '/') + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {})
        });
        await client.commit(items, jobId, new URLSearchParams(location.search).get('edit_group'));
        builderTrack('cart_success');
        window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
        return;
      }
      // Step 4: POST to Shopify cart (legacy standalone compatibility)
      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!cartRes.ok) {
        const err = await cartRes.json().catch(() => ({ description: 'Cart error' }));
        throw new Error(err.description || err.message || 'Cart add failed');
      }

      // Step 5: Update cart note with installation details + PDF URL
      const pdfUrlForNote = apiUrl('/api/jobs/' + jobId + '/pdf');
      try {
        const currentCart = await builderJson('/cart.js');
        const note = [currentCart.note || '', `SPINWELD INSTALLATION ORDER\nJob ID: ${jobId}\nTank: ${state.selectedTank.title}\nFittings: ${state.placedFittings.length}\nInstallation PDF: ${pdfUrlForNote}\n\nTech: Open PDF to see fitting placement diagram before installation.`].filter(Boolean).join('\n\n');
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note,
            attributes: { '_builder_source': 'spinweld-builder', 'Built With': 'ERVP Spinweld Builder' }
          })
        });
      } catch (noteErr) {
        console.warn('[SpinweldBuilder] Cart note update failed (non-fatal):', noteErr.message);
      }

      // Step 6: redirect to cart
      window.location.href = '/cart';
    } catch (err) {
      console.error('[SpinweldBuilder] Add to cart error:', err);
      builderTrack('cart_validation_failed');
      cartSubmitting = false;
      builderStatus('Could not add this build: ' + err.message, true);
      cartBtn.disabled = false;
      updateOrderSummary();
    }
  });
}

/* ── Window resize ───────────────────────────────────────────────────────────── */
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    if (state.selectedTank) drawAllViews();
  }, 100);
});

/* ── Init ────────────────────────────────────────────────────────────────────── */
async function init() {
  // Load tanks and fittings in parallel
  const [tanksRes, fittingsRes] = await Promise.allSettled([
    builderJson(apiUrl('/api/tanks')),
    builderJson(apiUrl('/api/fittings')),
  ]);

  if (tanksRes.status === 'fulfilled' && Array.isArray(tanksRes.value)) {
    // Never let a guessed 36 x 24 x 12 footprint reach the placement tool.
    // A hidden tank is safer than manufacturing from dimensions we did not verify.
    state.tanks = tanksRes.value.filter(tank => {
      const dims = tank && tank.dimensions;
      return !tank.dimensionsNote && dims && Number(dims.L) > 0 && Number(dims.W) > 0 && Number(dims.H) > 0;
    });
  } else {
    throw new Error('The tank catalog could not be loaded.');
  }

  if (fittingsRes.status === 'fulfilled' && Array.isArray(fittingsRes.value)) {
    state.fittings = fittingsRes.value.filter(f => f.placementEligible !== false && !/\b(kit|sealant|adhesive|tool|clamp|monitor|plug)\b|o[ -]?ring only|filter screens|pack of|repair service/i.test(f.title || ''));
  } else {
    throw new Error('The fitting catalog could not be loaded.');
  }

  calcDimRanges();
  updateCategoryTabs();
  renderTankGrid();
  updateDimPills();
  renderFittingList();
  setupCanvasEvents();
  setupDimFilterEvents();

  // Try to load job from URL
  const loaded = await loadJobFromUrl();
  if (!loaded) {
    if (!loadTankFitment()) loadTankFromUrl();
  }
  if (window.createSpinweldProject) {
    tankProject = window.createSpinweldProject({ state, json: builderJson, url: apiUrl, place: placeFitting, summary: updateOrderSummary,
      redraw() { drawAllViews(); updateMeasurementsList(); updateOrderSummary(); },
      clear() {
        state.selectedTank = null; state.placedFittings = []; state.jobId = null; state.jobName = null;
        document.getElementById('section-builder').classList.remove('active');
        document.getElementById('section-tanks').classList.add('active');
        const url = new URL(location.href); ['job', 'project', 'tank', 'edit_group'].forEach(key => url.searchParams.delete(key)); history.replaceState({}, '', url.pathname + url.search);
      },
      restore(build) {
        selectTank(build.tank); state.placedFittings = build.fittings.map(f => ({ ...f })); state.jobId = build.jobId; state.jobName = build.fields['input-job-name'] || null;
        drawAllViews(); updateMeasurementsList(); updateOrderSummary(); updateJobStatus();
      },
      saveBuild(build) {
        return builderJson(apiUrl('/api/jobs'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          job_name: build.fields['input-job-name'] || 'My Tank Build', tank_product_id: String(build.tank.id), tank_handle: build.tank.handle,
          tank_title: build.tank.title, tank_part_number: build.tank.sku || '', tank_dimensions: build.tank.dimensions, fittings: build.fittings, client_fitting_count: build.fittings.length,
          rv_make: build.fields['input-rv-make'] || undefined, rv_model: build.fields['input-rv-model'] || undefined, rv_year: build.fields['input-rv-year'] ? Number(build.fields['input-rv-year']) : undefined,
          fitment_confirmed: build.fitmentConfirmed,
        }) });
      }
    });
    await tankProject.init();
  }
}

// When loaded via embed.js the HTML is already injected before this script runs.
// When loaded directly via index.html the DOM is also ready. Either way, init().
(function() {
  var root = document.getElementById('spinweld-root') || document.body;  // mount context
  function safeInit() {
    init().catch(function(err) {
      console.error('[SpinweldBuilder] init() failed:', err);
      var grid = document.getElementById('tank-grid');
      if (grid) {
        grid.textContent = 'Unable to load the builder. Please refresh to try again.';
        grid.setAttribute('role', 'alert');
      }
    });
  }
  if (document.readyState === 'loading') {
    (root === document.body ? document : root).addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
